import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { hasAutoConversationFired } from './common/utils/hasAutoConversationFired';
import { markAutoConversationFired } from './common/utils/markAutoConversationFired';
import { useHasUserSentRovoChatMessage } from './controllers/traits';
import messages from './messages';

/**
 * This component is rendered only when userId is defined, ensuring useHasUserSentRovoChatMessage
 * is never called conditionally (which would violate Rules of Hooks) and that `undefined` returned
 * from the hook unambiguously means "new user with no trait set" rather than "userId not yet available".
 */
export const AutoConversationEvaluator = ({
	userId,
	firedRef,
}: {
	userId: string;
	firedRef: React.MutableRefObject<boolean>;
}) => {
	const { hasSentRovoChatMessage, loaded } = useHasUserSentRovoChatMessage(userId);
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	// Conditions to evaluate the `full_screen_chat_auto_conversations` experiment:
	// 1. firedRef is false — only evaluate once. Ref lives in the parent so it survives
	//    unmount/remount cycles (e.g. user navigates away mid-stream, isEmptyConvo toggles).
	// 2. hasAutoConversationFired() is false — localStorage guard (30-min TTL) that persists
	//    across full React remounts, page reloads, and new tabs. Prevents re-exposure during the
	//    window where the TAP trait hasn't updated yet after the user sent their first message.
	// 3. loaded is true — the TAP fetch has settled. Without this guard, the effect fires on the
	//    first render while the fetch is still in-flight (hasSentRovoChatMessage = undefined),
	//    which causes the experiment to evaluate before we know if the user has already sent a
	//    message. This was most visible in townsquare where the userStore hydrates async, giving
	//    the fetch even less time to resolve before the component mounts and the effect fires.
	// 4. hasSentRovoChatMessage is strictly NOT true — undefined (new user, no trait) and false
	//    both warrant evaluation; only true blocks it.
	const shouldEvaluateAutoConversation =
		!firedRef.current && !hasAutoConversationFired() && loaded && hasSentRovoChatMessage !== true;

	const fireExperimentAnalytics = (cohort: string) => {
		sendAnalyticsEvent({
			action: 'fired',
			actionSubject: 'autoConversationPrompt',
			attributes: {
				userId,
				experimentCohort: cohort,
				hasSentRovoChatMessage,
			},
		});
	};

	useEffect(() => {
		sendAnalyticsEvent({
			action: 'rendered',
			actionSubject: 'autoConversationEvaluator',
			attributes: {
				// If this is true, we will evaluate the experiment and potentially expose the user to it
				shouldEvaluateAutoConversation,
				userId,
				// Logging left side of shouldEvaluateAutoConversation: If this is true, we have already evaluated the experiment and will not evaluate it again
				autoConversationFiredRefCurrentValue: firedRef.current,
				// Logging right side of shouldEvaluateAutoConversation: If this is true, the TAP fetch has settled
				hasSentRovoChatMessageLoaded: loaded,
				// Logging right side of shouldEvaluateAutoConversation: If this is true, the user has already sent a message and we will NOT evaluate the experiment
				hasSentRovoChatMessage,
			},
		});
		if (shouldEvaluateAutoConversation) {
			// Set this to true so that we don't evaluate the experiment again. Because firedRef
			// lives in the parent, this persists across unmount/remount cycles of this component
			// (e.g. when the user navigates away mid-stream and isEmptyConvo toggles back to true).
			firedRef.current = true;
			// Also persist to localStorage (30-min TTL) so we don't re-expose the user if the
			// React tree fully remounts (e.g. page reload, tab reopen) before the TAP trait
			// (`segment_has_sent_rovo_chat_message_aaid`) has had time to update.
			markAutoConversationFired();
			switch (
				FeatureGates.getExperimentValue<string>(
					'full_screen_chat_auto_conversations',
					'cohort',
					'control',
				)
			) {
				case 'auto-trigger':
					requestAnimationFrame(() => {
						publish({
							type: 'chat-new',
							source: 'ai-mate-auto-conversations',
							data: {
								prompt: formatMessage(messages.autoConversationPrompt),
								dialogues: [],
							},
						});
					});
					fireExperimentAnalytics('auto-trigger');
					break;
				case 'manual-trigger':
					requestAnimationFrame(() => {
						publish({
							type: 'insert-prompt',
							source: 'ai-mate-auto-conversations',
							data: {
								prompt: formatMessage(messages.autoConversationPrompt),
								overrideAutoSend: true,
							},
						});
					});
					fireExperimentAnalytics('manual-trigger');
					break;
				case 'control':
					fireExperimentAnalytics('control');
					break;
				default:
					fireExperimentAnalytics('none');
					break;
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- firedRef.current is intentionally excluded (refs are mutable by design and should not be deps); hasSentRovoChatMessage is intentionally excluded (already captured by shouldEvaluateAutoConversation — including it causes the effect to fire twice per trait change)
	}, [
		shouldEvaluateAutoConversation,
		sendAnalyticsEvent,
		fireExperimentAnalytics,
		publish,
		formatMessage,
		userId,
	]);

	// manual-trigger: fire on unmount if prompt was inserted but user left without sending.
	// Measures prompt visibility — high rate may indicate cohort/placement needs revisiting.
	// FeatureGates.getExperimentValue in cleanup is safe: cohort is stable for the session.
	useEffect(() => {
		return () => {
			if (
				shouldEvaluateAutoConversation &&
				FeatureGates.getExperimentValue<string>(
					'full_screen_chat_auto_conversations',
					'cohort',
					'control',
				) === 'manual-trigger'
			) {
				sendAnalyticsEvent({
					action: 'dismissed',
					actionSubject: 'autoConversationPrompt',
					attributes: {
						userId,
						hasSentRovoChatMessage,
						// This fires when the prompt was inserted (firedRef is true) but the user
						// navigated away before sending — useful for measuring prompt visibility.
					},
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: this cleanup should only run on unmount, capturing the values at mount time via closure is correct here
	}, []);

	return null;
};
